// src/components/pipelines/nodes/EntryNode.jsx - Entry Point Node for React Flow
import React, { useState, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { Badge } from '../../ui/badge.tsx';
import { Input } from '../../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select.tsx';
import { Button } from '../../ui/button.tsx';
import { Play, Calendar, UserPlus, MousePointerClick, Edit2, Plus, X } from 'lucide-react';
import ConditionBuilder from '../ConditionBuilder.jsx';
import { TRIGGER_EVENT_ENTITIES } from '../../../config/workflowEntities';

// Friendly trigger event labels
const TRIGGER_EVENTS = [
  // Customer events
  { value: 'customer_created', label: 'New Customer Added', color: 'green' },
  { value: 'customer_updated', label: 'Customer Updated', color: 'blue' },
  { value: 'customer_field_updated', label: 'Custom Field Changed', color: 'purple' },

  // Document events
  { value: 'document_uploaded', label: 'Document Uploaded', color: 'cyan' },
  { value: 'document_removed', label: 'Document Removed', color: 'orange' },

  // Appointment events
  { value: 'appointment_created', label: 'New Appointment Booked', color: 'green' },
  { value: 'appointment_completed', label: 'Appointment Completed', color: 'green' },

  // Invoice events
  { value: 'invoice_sent', label: 'Invoice Sent to Customer', color: 'blue' },
  { value: 'invoice_paid', label: 'Invoice Payment Received', color: 'green' },

  // Manual approval events
  { value: 'manual_approval_completed', label: 'Manual Approval Completed', color: 'yellow' },
];

/**
 * EntryNode - Represents the start point of an automation flow
 *
 * Node data structure (BACKEND-ALIGNED):
 * {
 *   id: 'entry-uuid',
 *   type: 'entry',
 *   data: {
 *     isEntry: true,                        // ← Backend checks this flag!
 *     triggerEvent: 'customer_created',     // ← Renamed from 'eventType'
 *     conditions: [{field: '...', operator: '...', value: '...'}],
 *     label: 'New Customer Entry',
 *     description: 'Entry point description'
 *   }
 * }
 */
const EntryNode = ({ data, selected, id }) => {
  const {
    isEntry = true,
    triggerEvent,
    conditions = [],
    label,
    description
  } = data;
  const { updateNodeData } = useReactFlow();

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(description || '');
  const [isAddingCondition, setIsAddingCondition] = useState(false);
  const [newCondition, setNewCondition] = useState({ field: '', operator: '', value: '' });

  // Get available entities based on selected trigger event
  const availableEntities = useMemo(() => {
    if (!triggerEvent) return ['customer'];
    return TRIGGER_EVENT_ENTITIES[triggerEvent] || ['customer'];
  }, [triggerEvent]);

  // Get friendly label for trigger event
  const getTriggerLabel = (eventValue) => {
    const event = TRIGGER_EVENTS.find(e => e.value === eventValue);
    return event ? event.label : eventValue?.replace(/_/g, ' ') || 'Select trigger event';
  };

  // Get trigger config
  const triggerConfig = TRIGGER_EVENTS.find(e => e.value === triggerEvent) || {
    label: 'Event Trigger',
    color: 'green'
  };

  // Get static Tailwind class for handle color (dynamic classes don't work with JIT)
  const getHandleColorClass = (color) => {
    switch(color) {
      case 'green': return '!bg-green-500';
      case 'blue': return '!bg-blue-500';
      default: return '!bg-green-500';
    }
  };

  // Update trigger event
  const handleTriggerEventChange = (newTriggerEvent) => {
    updateNodeData(id, {
      ...data,
      isEntry: true,  // Always maintain isEntry flag
      triggerEvent: newTriggerEvent
    });
  };

  // Save description
  const handleSaveDescription = () => {
    updateNodeData(id, { ...data, description: editedDescription.trim() });
    setIsEditingDescription(false);
  };

  // Add condition
  const handleAddCondition = () => {
    if (newCondition.field && newCondition.operator && newCondition.value !== '') {
      const updatedConditions = [...conditions, newCondition];
      updateNodeData(id, { ...data, conditions: updatedConditions });
      setNewCondition({ field: '', operator: '', value: '' });
      setIsAddingCondition(false);
    }
  };

  // Handle condition change from builder
  const handleConditionChange = (updatedCondition) => {
    setNewCondition(updatedCondition);
  };

  // Remove condition
  const handleRemoveCondition = (index) => {
    const updatedConditions = conditions.filter((_, i) => i !== index);
    updateNodeData(id, { ...data, conditions: updatedConditions });
  };

  return (
    <>
      {/* No input handle - this is the start */}

      <div className={`
        w-56 rounded-lg border bg-slate-900
        ${selected ? `ring-2 ring-${triggerConfig.color}-500 shadow-lg border-${triggerConfig.color}-500/50` : `shadow-md border-${triggerConfig.color}-500/20`}
        transition-all duration-200 hover:shadow-xl
        bg-gradient-to-br from-${triggerConfig.color}-500/10 to-${triggerConfig.color}-600/5
        relative
      `}>
        {/* Header */}
        <div className="p-3 pb-2 border-b border-slate-700/50">
          <div className="flex items-center justify-between">
            <div className={`text-base font-semibold text-${triggerConfig.color}-100`}>
              Entry Point
            </div>
            <Badge variant="outline" className={`text-xs border-${triggerConfig.color}-500/50 text-${triggerConfig.color}-300`}>
              Trigger
            </Badge>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 pt-2 space-y-2">
          {/* Trigger Event Selector */}
          <Select value={triggerEvent || ''} onValueChange={handleTriggerEventChange}>
            <SelectTrigger className="h-auto text-xs bg-slate-800 border-slate-700 text-slate-200 nodrag py-2">
              <SelectValue placeholder="Select trigger event...">
                {triggerEvent && (
                  <span>{getTriggerLabel(triggerEvent)}</span>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {TRIGGER_EVENTS.map(event => (
                <SelectItem
                  key={event.value}
                  value={event.value}
                  className="text-slate-200 cursor-pointer"
                >
                  <span>{event.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Conditions List */}
          {conditions.length > 0 && (
            <div className="space-y-1">
              {conditions.map((condition, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-xs bg-slate-800/50 rounded px-2 py-1 border border-slate-700/50 nodrag"
                >
                  <div className="flex-1">
                    <span className="text-slate-300">{condition.field}</span>
                    <span className="text-slate-500 mx-1">{condition.operator}</span>
                    <span className="text-purple-300">{String(condition.value)}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveCondition(index)}
                    className="h-4 w-4 p-0 text-red-400 hover:text-red-300"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Add Condition */}
          {isAddingCondition ? (
            <div className="space-y-2 p-2 bg-slate-800/30 rounded border border-slate-700/50 nodrag">
              <ConditionBuilder
                availableEntities={availableEntities}
                condition={newCondition}
                onChange={handleConditionChange}
                size="compact"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleAddCondition}
                  className="flex-1 h-6 text-xs bg-green-600 hover:bg-green-700"
                  disabled={!newCondition.field || !newCondition.operator || newCondition.value === ''}
                >
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsAddingCondition(false);
                    setNewCondition({ field: '', operator: '', value: '' });
                  }}
                  className="flex-1 h-6 text-xs"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsAddingCondition(true)}
              className={`w-full h-7 text-xs border-${triggerConfig.color}-500/30 text-${triggerConfig.color}-300 hover:bg-${triggerConfig.color}-500/10 nodrag`}
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Condition
            </Button>
          )}

          {/* Editable Description */}
          <div className="border-t border-slate-700/50 pt-2">
            {isEditingDescription ? (
              <Input
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                onBlur={handleSaveDescription}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveDescription();
                  if (e.key === 'Escape') setIsEditingDescription(false);
                }}
                placeholder="Add description..."
                autoFocus
                className="text-xs bg-slate-800 border-slate-700 text-slate-200 nodrag"
              />
            ) : (
              <div
                className="flex items-center gap-2 justify-center cursor-pointer nodrag"
                onClick={() => {
                  setEditedDescription(description || '');
                  setIsEditingDescription(true);
                }}
              >
                {description ? (
                  <div className="text-xs text-slate-400 italic">{description}</div>
                ) : (
                  <div className="text-xs text-slate-500 italic">Add description...</div>
                )}
                <Edit2 className="w-3 h-3 text-slate-500 hover:text-slate-300" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Output handle (right side) - connects to first workflow node */}
      <Handle isConnectable={1}
        type="source"
        position={Position.Right}
        id="out"
        style={{ right: '-8px', zIndex: 1000 }}
        className={`w-3 h-3 ${getHandleColorClass(triggerConfig.color)}`}
      />
    </>
  );
};

export default EntryNode;
